import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { usePermissions } from '../../contexts/PermissionContext';
import { decodeJWT } from '../../utils/jwtDecoder';
import { ChevronDown, ChevronUp, Key, User, Shield, Info, Crown } from 'lucide-react';

const PermissionDebug: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();
  const { 
    permissions, 
    roles, 
    canReadGames, 
    canDownloadRegistrations, 
    canAccessCoopAdmin,
    isAnyAdmin,
    adminRoles,
    hasPermission,
    hasRole
  } = usePermissions();
  const [token, setToken] = useState<string>('');

  const loadToken = async () => {
    if (isAuthenticated) {
      try {
        const accessToken = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          },
        });
        setToken(accessToken);
      } catch (error) {
        console.error('Error getting token:', error);
      }
    }
  };

  const decodedToken = token ? decodeJWT(token) : null;

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg shadow-lg max-w-md">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-dark-700 rounded-t-lg"
        >
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-500" />
            <span className="font-medium text-gray-900 dark:text-gray-100">
              Permission Debug
            </span>
          </div>
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {isExpanded && (
          <div className="p-4 border-t border-gray-200 dark:border-dark-700 space-y-4">
            {/* User Info */}
            {user && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    User Information
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-dark-700 p-2 rounded space-y-1">
                  <div><span className="font-medium">Email:</span> {user.email}</div>
                  <div><span className="font-medium">Name:</span> {user.name}</div>
                  {user.nickname && <div><span className="font-medium">Nickname:</span> {user.nickname}</div>}
                  <div className="flex items-center gap-1">
                    <span className="font-medium">Admin Status:</span>
                    {isAnyAdmin ? (
                      <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                        <Crown className="w-3 h-3" />
                        Administrator
                      </span>
                    ) : (
                      <span className="text-gray-500">Regular User</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Quick Status */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className={`px-2 py-1 rounded ${canReadGames ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'}`}>
                Read Games: {canReadGames ? '✓' : '✗'}
              </div>
              <div className={`px-2 py-1 rounded ${canDownloadRegistrations ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'}`}>
                Download: {canDownloadRegistrations ? '✓' : '✗'}
              </div>
              <div className={`px-2 py-1 rounded col-span-2 ${canAccessCoopAdmin ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'}`}>
                Coop Admin: {canAccessCoopAdmin ? '✓' : '✗'}
              </div>
            </div>

            {/* Permissions */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Key className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Permissions ({permissions.length})
                </span>
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-dark-700 p-2 rounded max-h-24 overflow-y-auto">
                {permissions.length > 0 ? (
                  <div className="space-y-1">
                    {permissions.map((permission, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="font-mono">{permission}</span>
                        <span className={`ml-2 px-1 rounded text-xs ${
                          hasPermission(permission) 
                            ? 'bg-green-200 text-green-800 dark:bg-green-900/40 dark:text-green-400' 
                            : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
                        }`}>
                          {hasPermission(permission) ? '✓' : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="italic">No permissions found</div>
                )}
              </div>
            </div>

            {/* Roles */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Roles ({roles.length})
                </span>
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-dark-700 p-2 rounded">
                {roles.length > 0 ? (
                  <div className="space-y-1">
                    {roles.map((role, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="font-mono">{role}</span>
                        <div className="flex items-center gap-1">
                          {adminRoles.includes(role) && (
                            <Crown className="w-3 h-3 text-amber-500" />
                          )}
                          <span className={`px-1 rounded text-xs ${
                            hasRole(role) 
                              ? 'bg-blue-200 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400' 
                              : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
                          }`}>
                            {hasRole(role) ? '✓' : '—'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="italic">No roles found</div>
                )}
              </div>
            </div>

            {/* Admin Roles Summary */}
            {adminRoles.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Admin Roles ({adminRoles.length})
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                  <div className="space-y-1">
                    {adminRoles.map((role, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Crown className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                        <span className="font-mono text-amber-800 dark:text-amber-300">{role}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Token Actions */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={loadToken}
                className="text-xs px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/30"
              >
                Load Token
              </button>
              <button
                onClick={() => setShowToken(!showToken)}
                className="text-xs px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                disabled={!token}
              >
                {showToken ? 'Hide' : 'Show'} Token
              </button>
              <button
                onClick={() => {
                  (window as any).debugTokenAuth?.();
                }}
                className="text-xs px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 rounded hover:bg-green-200 dark:hover:bg-green-900/30"
              >
                Debug Console
              </button>
            </div>

            {/* Token Display */}
            {showToken && token && (
              <div className="text-xs space-y-3">
                {decodedToken && (
                  <div>
                    <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Auth-Related Claims Only:
                    </div>
                    <div className="bg-gray-50 dark:bg-dark-700 p-2 rounded text-gray-600 dark:text-gray-400 max-h-32 overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap">
                        {JSON.stringify(
                          Object.keys(decodedToken)
                            .filter(key => 
                              key.includes('role') || 
                              key.includes('permission') || 
                              key === 'scope' || 
                              key.includes('https://')
                            )
                            .reduce((obj, key) => {
                              obj[key] = decodedToken[key];
                              return obj;
                            }, {} as any),
                          null, 2
                        )}
                      </pre>
                    </div>
                  </div>
                )}
                
                <div>
                  <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full JWT Token:
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-700 p-2 rounded font-mono text-gray-600 dark:text-gray-400 max-h-32 overflow-y-auto break-all">
                    {token}
                  </div>
                </div>

                {decodedToken && (
                  <div>
                    <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                      All Decoded Claims:
                    </div>
                    <div className="bg-gray-50 dark:bg-dark-700 p-2 rounded text-gray-600 dark:text-gray-400 max-h-32 overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap">
                        {JSON.stringify(decodedToken, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PermissionDebug;
